'use client';

import classnames from 'classnames';
import Image from 'next/image';

import ShowingTagImage from '@/app/[locale]/(default-layout)/specials/_components/ShowingTagImage';

import { ShowingTagDto } from '@cineamo/api/models/showing-tag/ShowingTagDto.types';

import { ShowingApiGroup } from '@cineamo/api/resources/showing/showing-api.group';

import { useCachedShowingData } from '@/hooks/useCachedShowingData';
import { useFutureShowings } from '@/hooks/useFutureShowings';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ShowingTagCoverProps = {
    showingTag: ShowingTagDto;
    showingsCount: number;
};

export default function ShowingTagCover(props: ShowingTagCoverProps) {
    const { showingTag, showingsCount } = props;
    const domainConfiguration = useDomainConfiguration();
    const { track } = useAnalytics();
    const cinema = domainConfiguration.cinema;
    const cinemaId = cinema.id;

    const { data: showingsWithMovieResponse } = useFutureShowings(
        cinemaId,
        {
            group: ShowingApiGroup.Content
        },
        {
            showingTagIds: [showingTag.id]
        }
    );

    const centeredPosterData = useCachedShowingData(
        showingsWithMovieResponse?.[0],
        showingsWithMovieResponse?.[0]?._embedded?.movie
    );
    const leftPosterData = useCachedShowingData(
        showingsWithMovieResponse?.[1],
        showingsWithMovieResponse?.[1]?._embedded?.movie
    );
    const rightPosterData = useCachedShowingData(
        showingsWithMovieResponse?.[2],
        showingsWithMovieResponse?.[2]?._embedded?.movie
    );

    const data = [centeredPosterData, leftPosterData, rightPosterData];
    const availableImages = data.filter((data) => data?.posterImageURL_W780);

    let centerPoster = null;
    let leftPoster = null;
    let rightPoster = null;

    if (availableImages.length === 3) {
        centerPoster = availableImages[0];
        leftPoster = availableImages[1];
        rightPoster = availableImages[2];
    } else if (availableImages.length === 2) {
        leftPoster = availableImages[0];
        rightPoster = availableImages[1];
    } else if (availableImages.length === 1) {
        centerPoster = availableImages[0];
    }

    const background = centerPoster ?? leftPoster ?? rightPoster;

    const backgroundSrc = background?.posterBlurDataImage || background?.posterImageURL_W780;
    const centerPosterSrc = centerPoster?.posterImageURL_W780;
    const leftPosterSrc = leftPoster?.posterImageURL_W780;
    const rightPosterSrc = rightPoster?.posterImageURL_W780;

    const hasImages = !!(centerPoster || leftPoster || rightPoster || background);

    return (
        <div className="relative flex w-full h-full aspect-[16/9] flex-none select-none">
            <div
                className={classnames(
                    'absolute left-0 top-0 w-full h-full flex flex-row z-0 opacity-0',
                    showingsCount && hasImages ? 'group-hover:opacity-100 duration-300' : undefined
                )}
                onMouseEnter={() => {
                    // works on desktop only
                    track('Special Card Hover', {
                        specialId: showingTag.id
                    });
                }}>
                {backgroundSrc && (
                    <Image
                        className={classnames(
                            'absolute left-0 top-0 w-full h-full object-cover z-0 gradient-mask-b-70 opacity-40 blur'
                        )}
                        src={backgroundSrc}
                        width={800}
                        height={600}
                        alt={`${background?.name} Backdrop`}
                    />
                )}
                {leftPosterSrc && (
                    <Image
                        className={classnames(
                            'absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-auto h-[165px] aspect-[2/3] object-cover rounded-4',
                            'scale-50 group-hover:scale-90 group-hover:left-1/4 duration-300'
                        )}
                        src={leftPosterSrc}
                        width={600}
                        height={800}
                        alt={`${leftPoster?.name} Poster`}
                    />
                )}
                {centerPosterSrc && (
                    <Image
                        className={classnames(
                            'absolute left-1/2 top-1/2 -translate-y-1/2 -translate-x-1/2 w-auto h-[165px] aspect-[2/3] object-cover rounded-4 z-10',
                            'scale-50 group-hover:scale-100 duration-300'
                        )}
                        src={centerPosterSrc}
                        width={800}
                        height={1000}
                        alt={`${centerPoster?.name} Poster`}
                    />
                )}
                {rightPosterSrc && (
                    <Image
                        className={classnames(
                            'absolute right-1/2 top-1/2 -translate-y-1/2 translate-x-1/2 w-auto h-[165px] aspect-[2/3] object-cover rounded-4',
                            'scale-50 group-hover:scale-90 group-hover:right-1/4 duration-300'
                        )}
                        src={rightPosterSrc}
                        width={600}
                        height={800}
                        alt={`${rightPoster?.name} Poster`}
                    />
                )}
            </div>
            <div
                className={classnames(
                    'relative flex w-full h-full z-20 duration-300',
                    showingsCount > 0 && hasImages ? 'group-hover:opacity-0' : 'group-hover:scale-105'
                )}>
                <ShowingTagImage
                    className="h-full"
                    showingTag={showingTag}
                />
            </div>
        </div>
    );
}
